import { useTranslation } from 'react-i18next';

import { showToast } from '@features/ToastManager';
import { routes } from '@shared/api/routes';
import { UiButton, UiText } from '@shared/ui';

import cls from './BonusTabContent.module.css';
import { copyTextToClipboard } from '../../lib/copyTextToClipboard';
import { PROFILE_TOAST_SCOPE } from '../../model/toast';

import type { Profile } from '@entities/profile';

type BonusTabContentProps = {
    profile: Profile;
};

export const BonusTabContent = ({ profile }: BonusTabContentProps) => {
    const { t } = useTranslation();
    const referralUrl = profile.referral.url;

    const handleCopyReferralUrl = async () => {
        if (!referralUrl) {
            return;
        }

        try {
            await copyTextToClipboard(referralUrl);
            showToast(t('profile.page.bonus.copySuccess'), {
                scope: PROFILE_TOAST_SCOPE,
            });
        } catch {
            showToast(t('profile.page.bonus.copyError'), {
                scope: PROFILE_TOAST_SCOPE,
                type: 'error',
            });
        }
    };

    return (
        <div className={cls.bonusContent}>
            <div className={cls.bonusDescription}>
                <UiText>{t('profile.page.bonus.description')}</UiText>
                <UiText>
                    {t('profile.page.bonus.rewardText')}{' '}
                    <UiButton
                        type="button"
                        variant="text"
                        className={cls.inlineButton}
                        btnText={t('profile.page.bonus.contractLink')}
                        onClick={() => window.location.assign(routes.profileReferralProgram())}
                    />
                    {t('profile.page.bonus.rewardTextSuffix')}
                </UiText>
            </div>

            <div className={cls.referralRow}>
                <UiText tag="span">{t('profile.page.bonus.referralLink.label')}:</UiText>
                <a href={referralUrl} className={cls.referralLink} target="_blank" rel="noreferrer">
                    <UiText tag="span" weight="bold">
                        {referralUrl}
                    </UiText>
                </a>
                <UiButton
                    type="button"
                    variant="text"
                    className={cls.inlineButton}
                    btnText={t('profile.page.bonus.copy')}
                    disabled={!referralUrl}
                    onClick={handleCopyReferralUrl}
                />
            </div>

            <div className={cls.bonusStats}>
                <div className={cls.bonusStat}>
                    <UiText variant="heading" weight="bold">
                        {profile.referral.invitedCount}
                    </UiText>
                    <UiText variant="subPlain" weight="bold">
                        {t('profile.page.bonus.stats.invited')}
                    </UiText>
                </div>

                <div className={cls.bonusStat}>
                    <UiText variant="heading" weight="bold">
                        {profile.referral.paidCount}
                    </UiText>
                    <UiText variant="subPlain" weight="bold">
                        {t('profile.page.bonus.stats.paid')}
                    </UiText>
                </div>
            </div>
        </div>
    );
};
